import React, { useCallback, useState } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { buildToastCopyText } from '../utils/appToast';

type ToastCodeCaptionProps = {
  code?: string;
  requestId?: string;
  jobId?: string;
  errorCode?: string;
  /** Main toast/banner body included when copying (title, segments, progress, etc.). */
  copyBody?: string;
};

/** Shown under toast body so users can quote a code in bug reports; includes Copy for full text. */
export function ToastCodeCaption({
  code,
  requestId,
  jobId,
  errorCode,
  copyBody
}: ToastCodeCaptionProps) {
  const trimmed = String(code || '').trim();
  const req = String(requestId || '').trim();
  const job = String(jobId || '').trim();
  const err = String(errorCode || '').trim();
  const body = String(copyBody || '').trim();
  const hasMeta = Boolean(trimmed || req || job || err);
  const [copied, setCopied] = useState(false);

  const copyText = buildToastCopyText({
    body,
    code: trimmed,
    requestId: req,
    jobId: job,
    errorCode: err
  });

  const onCopy = useCallback(async () => {
    if (!copyText) return;
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for older browsers / denied clipboard
      try {
        const ta = document.createElement('textarea');
        ta.value = copyText;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      } catch {
        // ignore
      }
    }
  }, [copyText]);

  if (!hasMeta && !body) return null;

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.25}
      sx={{ mt: 0.25, width: '100%' }}
    >
      {hasMeta ? (
        <Typography
          component="div"
          variant="caption"
          sx={{
            flex: 1,
            minWidth: 0,
            opacity: 0.9,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            letterSpacing: 0.02,
            wordBreak: 'break-word'
          }}
        >
          {[
            trimmed ? `Frontend trace: ${trimmed}` : '',
            req ? `Request id: ${req}` : '',
            job ? `Job id: ${job}` : '',
            err ? `Error code: ${err}` : ''
          ]
            .filter(Boolean)
            .join(' | ')}
        </Typography>
      ) : (
        <Typography component="div" variant="caption" sx={{ flex: 1, opacity: 0.75 }}>
          Copy full message
        </Typography>
      )}
      <Tooltip title={copied ? 'Copied' : 'Copy full message (incl. ids)'}>
        <IconButton
          size="small"
          color="inherit"
          aria-label="Copy full message including ids"
          onClick={() => void onCopy()}
          sx={{ mt: -0.25, opacity: 0.95 }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
